// https://github.com/vimeo/player.js
import {Mediaplayer} from 'modules/generic/mediaplayer/views/Mediaplayer';
import i18n from 'modules/utils/i18n';


const
	SELECTOR_LINK = 'a',
	CLASS_PLAYER = 'player',
	CLASS_LOADING = 'is-loading',
	CLASS_INITIALIZED = 'is-initialized',
	CLASS_AUTOPLAY = 'autoplay',
	CLASS_PLAYING = 'is-playing',
	PLAYER_OPTIONS = {
		autoplay: true,
		playsinline: true,
		color: 'ff6000'
	},
	PROGRESS_THRESHOLDS = [0.25, 0.5, 0.75, 1]
;


export class Vimeoplayer extends Mediaplayer {

	constructor(options) {
		super(options);

		this._resetProgress();
		this._isAutoplay = this.el.classList.contains(CLASS_AUTOPLAY);

		this._onClick = this._onClick.bind(this);
		this._onTimeupdate = this._onTimeupdate.bind(this);
		this._onPlay = this._onPlay.bind(this);
		this._onPause = this._onPause.bind(this);
		this._onEnded = this._onEnded.bind(this);

		if (this._isAutoplay) {
			this._load();
		}
	}

	get id() {
		this._id = this._id || this.el.getAttribute('data-id');
		return this._id;
	}

	render() {
		this._setup();
		return super.render();
	}

	play() {
		if (!this._player) {
			this._load();
			return;
		}

		super.play();
		this.el.classList.add(CLASS_PLAYING);
		this._player.play();
	}

	pause() {
		if (!this._player) {
			return;
		}

		this.el.classList.remove(CLASS_PLAYING);
		this._player.pause();
		super.pause();
	}

	_setup() {
		const link = this.el.querySelectorAll(SELECTOR_LINK)[0];
		link.setAttribute('role', 'button');
		link.setAttribute('aria-label', i18n.t('ui.video.play'));
		link.addEventListener('click', this._onClick);
		this._link = link;
	}

	_create(Player) {
		const
			element = document.createElement('div'),
			options = {
				...PLAYER_OPTIONS,
				loop: this._isAutoplay,
				background: this._isAutoplay,
				controls: !this._isAutoplay,
				autopause: !this._isAutoplay,
				id: this.id,
				dnt: true
			},
			player = new Player(element, options)
		;

		element.className = CLASS_PLAYER;

		if (this._isAutoplay) {
			this._intersectionObserver = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						this.play();
					} else {
						this.pause();
					}
				});
			});
		}

		player.ready().then(() => {
			this.el.classList.add(CLASS_INITIALIZED);
			this.el.classList.remove(CLASS_LOADING);

			Promise.all([
				player.getVideoWidth(),
				player.getVideoHeight()
			]).then(([width, height]) => {
				this.el.style.setProperty('--video-width', width);
				this.el.style.setProperty('--video-height', height);
			});

			if (this._isAutoplay) {
				this._intersectionObserver.observe(element);
			}

			player.on('timeupdate', this._onTimeupdate);
			player.on('play', this._onPlay);
			player.on('pause', this._onPause);
			player.on('ended', this._onEnded);
		});

		this._playerEl = element;
		this._player = player;
		this._link.parentNode.appendChild(element);
		this._link.removeEventListener('click', this._onClick);
	}

	_load() {
		if (this._player) {
			return;
		}

		if (this._request) {
			return;
		}

		/* eslint-disable no-empty */
		try {
			this.el.classList.add(CLASS_LOADING);
			this._link.disabled = true;
		} catch {}

		this._request = this.options.loader.request()
			.then(this._create.bind(this))
			.catch((error) => window.console && window.console.warn(error));

		this.context.trigger('vimeoplayer:load', {percent: 0});
	}

	_unload() {
		if (!this._player) {
			return;
		}

		this.el.style.paddingTop = 'unset';

		this._player.destroy()
			.then(() => this._destroyPlayer())
			.catch((error) => {
				window.console && window.console.warn(error);
				// We don't care about that error and fully remove all player
				// related elements and references from DOM.
				this._destroyPlayer();
			});
	}

	_resetProgress() {
		this._progress = [].concat(PROGRESS_THRESHOLDS);
	}

	_destroyPlayer() {
		if (this._intersectionObserver) {
			this._intersectionObserver.unobserve(this.playerEl);
		}

		this._intersectionObserver = null;
		this._request = null;
		this._playerEl.parentNode.removeChild(this._playerEl);
		this._playerEl = null;
		this._player = null;

		this.el.classList.remove(CLASS_INITIALIZED);
		this.el.classList.remove(CLASS_LOADING);

		// Wait for new initialization:
		this._link.disabled = false;
		this._link.addEventListener('click', this._onClick);
	}

	_onClick(event) {
		event.preventDefault();
		event.stopPropagation();
		this.play();
	}

	_onTimeupdate(event) {
		const {percent} = event;
		let index, step;

		for (index = 0; index < this._progress.length; index++) {
			step = this._progress[index];
			if (percent >= step) {
				this._progress.shift();
				this.context.trigger('vimeoplayer:progress', {percent: step});
				index--;
			}
		}
	}

	_onPlay() {
		if (this._isAutoplay) {
			return;
		}

		this.play();
	}

	_onPause(event) {
		const {percent} = event;

		this.pause();
		this.context.trigger('vimeoplayer:pause', {
			percent: PROGRESS_THRESHOLDS.reduce((step, value) => {
				return (step <= percent && percent < value) ? step : value;
			}, 0)
		});
	}

	_onEnded() {
		this.pause();
		this._resetProgress();
		this._unload();
	}

}
